<template>
  <div class="masonry-gallery">
    <template v-for="item in eventImages" :key="item?.id">
      <div class="masonry-item relative">
        <LazyReviewVideoPlayer
          v-if="item.media_type === 'video'"
          :source="item.original"
          :source-stream="item.streaming_url"
          :poster="item.thumb || item.cropped"
          class="w-full rounded-lg"
        />
        <NuxtLazyImg
          v-else
          class="w-full"
          :height="'auto'"
          :src="String(item?.cropped)"
          :alt="item?.alt"
          aspect-ratio="auto"
          sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
          @click="$emit('click:image')"
        />
        <div
          v-if="hasReviewOverlay(item)"
          class="absolute bottom-0 left-0 z-10 flex gap-2 p-2"
        >
          <div
            class="flex items-center gap-2 rounded-lg bg-stone-75 px-2 py-1.5 text-teal-900"
          >
            <span class="text-sm font-medium">{{ item.customerName }}</span>
            <ReviewRatingStars
              :average-rating="item.rating ?? 0"
              star-classes="h-3.5 w-3.5"
            />
          </div>
          <button
            type="button"
            class="rounded-lg bg-stone-75 px-2 py-1.5 text-teal-900 underline cursor-pointer hover:bg-stone-100 transition-colors"
            @click.stop.prevent="onSeeReviewClick(item)"
          >
            <span class="text-sm">{{
              t('product.event.customerMedia.seeReview')
            }}</span>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import type { EventDescription, GalleryItem } from '~/composables/types/event'
import ReviewRatingStars from '~/components/review/ReviewRatingStars.vue'

export type GalleryItemWithReview = GalleryItem & {
  customerName?: string
  rating?: number
  reviewId?: string
}

const props = defineProps<{
  event?: EventDescription
  images?: GalleryItemWithReview[]
}>()

const emit = defineEmits<{
  (e: 'click:image'): void
  (e: 'click:review', reviewId: string): void
}>()

const { t } = useI18n()

const hasReviewOverlay = (item: GalleryItemWithReview) => {
  return (
    item?.customerName != null &&
    item.customerName !== '' &&
    item?.rating != null
  )
}

const onSeeReviewClick = (item: GalleryItemWithReview) => {
  if (item.reviewId) {
    emit('click:review', item.reviewId)
  }
}

const eventImages = computed((): GalleryItemWithReview[] => {
  // If images prop is provided, use it (for review media)
  if (props.images) {
    return props.images
  }
  // Otherwise, use event prop (existing behavior)
  if (props.event) {
    return eventGetters.getGalleryImages(props.event) as GalleryItemWithReview[]
  }
  return []
})
</script>

<style scoped lang="postcss">
.masonry-gallery {
  @apply columns-1 md:columns-2;
  column-gap: 1rem; /* gap-4 = 1rem */
}

.masonry-item {
  @apply break-inside-avoid mb-4;
  display: inline-block;
  width: 100%;
}
</style>
